import {
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  fetchSlaInstances,
  fetchSlaPreferences,
  getOrganizationCalendar,
  patchSlaPreferences,
  toSlaApiError,
  type SlaApiError,
} from "api";
import { getTranslation } from "common";
import dayjs from "dayjs";
import { useSnackbar, useTranslations } from "hooks";
import {
  SLA_ACTIVITIES_TEXT_KEYS,
  SLA_ACTIVITY_PREFERENCE_FIELDS,
  SLA_ACTIVITY_PREFERENCE_KEYS,
  SLA_ACTIVITY_PREFERENCE_TEXT_KEYS,
  slaActivityPreferenceValue,
  slaActivityPreferencesScope,
  type SlaActivityPreferenceKey,
} from "model/slaActivitiesModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { SlaUserPreferencesDto, SlaUserPreferencesInput } from "model/slaModel";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

/**
 * **Fuso orario e "Notifiche SLA" dell'utente corrente** nella scheda
 * "Impostazioni SLA" dell'organizzazione (spostati da "Le mie attivita'",
 * `SlaActivitiesSurface.tsx`, flag `SHOW_STRIP_HEADER`/`SHOW_PREFERENCES`).
 *
 * - **Fuso orario**: stessa chiave e stesso chip della pagina attivita'
 *   (`sla.monitor.timezone`). La fonte preferita e' il **calendario
 *   dell'organizzazione** (`GET /calendar/organizations/{id}`, `timezoneId`): e'
 *   la timezone di organizzazione da cui il Server calcola le scadenze, ed e'
 *   leggibile anche senza istanze aperte (la pagina attivita' la prendeva dal
 *   `timezone_id` della riga del banner). Se il calendario non e' leggibile si
 *   ripiega sul `timezone_id` della prima istanza SLA (`data-timezone-source`).
 *   `SlaSettingsDto` non porta una timezone.
 * - **Preferenze**: le tre dell'utente autenticato per questa organizzazione
 *   (`GET|PATCH /sla/preferences?idorganization=`, l'utente lo identifica il
 *   JWT). L'organizzazione e' quella della pagina (id della rotta), non quella
 *   selezionata globalmente. Nessun autosalvataggio: gli interruttori entrano in
 *   una bozza e il "Salva" della pagina chiama `save()` dal ref, che manda **un**
 *   PATCH con i soli campi cambiati e poi rilegge. Il saluto/banner ("Ignora",
 *   `greeting_dismissed`) non fa parte di questo blocco: e' nascosto anche in
 *   "Le mie attivita'" (`SHOW_ACTIVITY_BANNER`).
 */
interface SlaOrganizationUserPreferencesProps {
  idorganization: number;
  /** Stato del salvataggio per il pulsante della pagina. */
  onSaveStateChange?: (state: SlaPreferencesSaveState) => void;
}

/** Stato esposto alla pagina che ospita il blocco. */
export interface SlaPreferencesSaveState {
  /** Bozza diversa dalla lettura del Server. */
  dirty: boolean;
  /** Lettura o scrittura in corso. */
  busy: boolean;
}

/** Handle per il "Salva" della pagina. */
export interface SlaOrganizationUserPreferencesHandle {
  /**
   * Manda il PATCH dei soli campi cambiati (nulla se la bozza e' pulita).
   * `notify` falso quando l'esito lo comunica gia' un'altra scrittura.
   * `false` se il Server rifiuta.
   */
  save: (notify?: boolean) => Promise<boolean>;
}

type PreferencesDraft = Record<SlaActivityPreferenceKey, boolean>;

const toDraft = (preferences: SlaUserPreferencesDto): PreferencesDraft => ({
  digest: slaActivityPreferenceValue(preferences, "digest"),
  escalation: slaActivityPreferenceValue(preferences, "escalation"),
  transactional: slaActivityPreferenceValue(preferences, "transactional"),
});

type TimezoneSource = "calendar" | "instances" | "";

const PREFIX = "organization-sla";

const SlaOrganizationUserPreferences = forwardRef<
  SlaOrganizationUserPreferencesHandle,
  SlaOrganizationUserPreferencesProps
>(function SlaOrganizationUserPreferences({ idorganization, onSaveStateChange }, ref) {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [preferences, setPreferences] = useState<SlaUserPreferencesDto | null>(null);
  const [draft, setDraft] = useState<PreferencesDraft | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);
  /** Errore dell'ultimo salvataggio: sopravvive alla rilettura che lo segue. */
  const [saveError, setSaveError] = useState<SlaApiError | null>(null);
  const [timezone, setTimezone] = useState<{ id: string; source: TimezoneSource }>({
    id: "",
    source: "",
  });

  const t = useCallback(
    (key: string): string => getTranslation(translations, key),
    [translations],
  );

  /** Lettura: nessuna scrittura implicita. */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchSlaPreferences(idorganization);
      setPreferences(response);
      setDraft(response ? toDraft(response) : null);
      setError(null);
    } catch (caught) {
      setPreferences(null);
      setDraft(null);
      setError(toSlaApiError(caught));
    } finally {
      setLoading(false);
    }
  }, [idorganization]);

  useEffect(() => {
    load();
  }, [load]);

  /** Timezone: calendario dell'organizzazione, poi la prima istanza SLA. */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const calendar = await getOrganizationCalendar(idorganization, today, today);
        if (calendar?.timezoneId) {
          if (mounted) setTimezone({ id: calendar.timezoneId, source: "calendar" });
          return;
        }
      } catch {
        // Calendario non leggibile: si ripiega sulle istanze.
      }
      try {
        const response = await fetchSlaInstances(idorganization);
        const fromInstance = response?.instances?.find((row) => row.timezone_id)?.timezone_id;
        if (mounted) {
          setTimezone(fromInstance ? { id: fromInstance, source: "instances" } : { id: "", source: "" });
        }
      } catch {
        // Nessuna timezone dichiarata: il chip non si rende.
      }
    })();
    return () => {
      mounted = false;
    };
  }, [idorganization]);

  const dirty =
    Boolean(preferences && draft) &&
    SLA_ACTIVITY_PREFERENCE_KEYS.some(
      (key) => preferences && draft && draft[key] !== slaActivityPreferenceValue(preferences, key),
    );

  /** Un solo PATCH con i soli campi cambiati, poi rilettura. */
  const save = async (notify = true): Promise<boolean> => {
    if (!preferences || !draft || !dirty) return true;
    const body: SlaUserPreferencesInput = {};
    SLA_ACTIVITY_PREFERENCE_KEYS.forEach((key) => {
      if (draft[key] !== slaActivityPreferenceValue(preferences, key)) {
        (body as Record<string, boolean>)[SLA_ACTIVITY_PREFERENCE_FIELDS[key]] = draft[key];
      }
    });
    setLoading(true);
    setSaveError(null);
    try {
      const response = await patchSlaPreferences(idorganization, body);
      if (notify && response?.responsemessage) {
        sendSnack({ message: response.responsemessage, type: "success" });
      }
      return true;
    } catch (caught) {
      setSaveError(toSlaApiError(caught));
      return false;
    } finally {
      await load();
    }
  };

  useImperativeHandle(ref, () => ({ save }));

  useEffect(() => {
    onSaveStateChange?.({ dirty, busy: loading });
  }, [onSaveStateChange, dirty, loading]);

  const forbidden = error?.status === 403 || error?.status === 401;

  return (
    <Stack spacing={1.5} data-testid={`${PREFIX}-user-settings`}>
      {timezone.id && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            data-testid={`${PREFIX}-timezone`}
            data-timezone={timezone.id}
            data-timezone-source={timezone.source}
            label={`${t(SLA_MONITOR_TEXT_KEYS.timezone)}: ${timezone.id}`}
            sx={{ alignSelf: "flex-start" }}
          />
        </Stack>
      )}

      {/* Ambito `user:<id>@organization:<id>`: solo come attributo, mai testo. */}
      <Stack
        spacing={1}
        data-testid={`${PREFIX}-preferences`}
        data-scope={preferences ? slaActivityPreferencesScope(preferences) : ""}
        data-dirty={dirty ? "true" : "false"}
        data-preferences-source="sla/preferences"
      >
        <Typography variant="subtitle2">{t(SLA_ACTIVITIES_TEXT_KEYS.preferencesTitle)}</Typography>

        {loading && <CircularProgress size={16} data-testid={`${PREFIX}-preferences-loading`} />}

        {error && (
          <Alert
            severity={forbidden ? "warning" : "error"}
            data-testid={`${PREFIX}-preferences-error`}
            data-error-status={error.status === null ? "" : String(error.status)}
          >
            {t(
              forbidden
                ? SLA_ACTIVITIES_TEXT_KEYS.preferencesForbidden
                : SLA_ACTIVITIES_TEXT_KEYS.preferencesError,
            )}
          </Alert>
        )}

        {saveError && (
          <Alert
            severity="error"
            data-testid={`${PREFIX}-preferences-save-error`}
            data-error-status={saveError.status === null ? "" : String(saveError.status)}
          >
            {t(saveError.messageKey ?? SLA_ACTIVITIES_TEXT_KEYS.preferencesError)}
          </Alert>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 3 }} flexWrap="wrap">
          {SLA_ACTIVITY_PREFERENCE_KEYS.map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  size="small"
                  checked={draft ? draft[key] : false}
                  disabled={!draft || loading}
                  onChange={({ target }) =>
                    setDraft((current) => (current ? { ...current, [key]: target.checked } : current))
                  }
                  inputProps={{
                    "aria-label": t(SLA_ACTIVITY_PREFERENCE_TEXT_KEYS[key]),
                    // Vedi `SlaActivitiesSurface.tsx`: testid sull'input reale.
                    ...({ "data-testid": `${PREFIX}-pref-${key}` } as Record<string, string>),
                  }}
                />
              }
              label={<Typography variant="body2">{t(SLA_ACTIVITY_PREFERENCE_TEXT_KEYS[key])}</Typography>}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
});

export default SlaOrganizationUserPreferences;
